import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface CampaignsState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	idLogo: string | undefined
	campaignName: string | undefined
	campaignLogo: string | undefined
	enabled: boolean
	deleted: boolean
	authorizationType: string | undefined
}

export interface BankAccountState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	contoCorrente: string | undefined
	senderAccount: string | undefined
	note: string | undefined
	enabled: boolean
	deleted: boolean
}

export interface CorporatesState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number | undefined
	id: number | undefined
	corporateSenderId: string | undefined
	enabled: boolean | undefined
	corporateSenderName: string | undefined
	bank: string | undefined
	campaigns: CampaignsState[]
	contiCorrenti: BankAccountState[]
	logo: string | undefined
	primaryColor: string | undefined
	secondaryColor: string | undefined
	deleted: boolean | undefined
	autoAccept: boolean | undefined
	timeToExpiry: number | undefined
	hasBankAuthorizer: boolean | undefined
	whatsapp: boolean | undefined
	daysToFirstReminder: number | undefined
	daysToSecondReminder: number | undefined
	checkIban: boolean | undefined
	plickOpen: boolean | undefined
	voucher: boolean | undefined
	fileVersion: number | undefined
}

export interface PageableElement {
	content: CorporatesState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: CorporatesState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface CorporatesListResponse {
	nextUrl: null | string
	page: PageElement
}

export interface CorporatesListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: CorporatesListResponse | []
}

export interface AllCorporatesListState {
	loading: boolean
	error: undefined | AxiosError<string>
	list: CorporateEssential[]
	selected: CorporateEssential | undefined
}

export interface CorporatesPayload {
	filter?: any | undefined
	sort?: any | undefined
	page?: number | undefined
	pageSize?: number | undefined
}

export interface FetchCorporatesPayload {
	apiName: string
	url: string
}

export interface CorporatesEssentialPayload {
	filter?: any | undefined
	sort?: any | undefined
}

export interface CorporateEssential {
	id: string
	name: string
	img: undefined | string
	primaryColor: undefined | string
	secondaryColor: undefined | string
	isPlickOpen: boolean
	enabled: boolean
	deleted: boolean
	bankId: string
	campaigns: CampaignsState[]
	contiCorrenti: BankAccountState[]
}

export interface ICreateResponse {}

interface IPatchPayload {
	idBank: string
	idCorporate: string
	corporateData: CorporatesState
}

let cancelTokenFetch = null

export const getUserAllCorporates = createAsyncThunk<
	CorporateEssential[],
	CorporatesEssentialPayload,
	{ rejectValue: string }
>(
	"banks/getUserAllCorporates",
	async (
		{ filter, sort }: CorporatesEssentialPayload,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicorporate/v2/getUserAllCorporates`
			const parameters = new URLSearchParams()

			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<CorporateEssential[]> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<CorporateEssential[]>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const getCorporatesPaginated = createAsyncThunk<
	CorporatesListResponse,
	CorporatesPayload,
	{ rejectValue: string }
>(
	"corporates/getCorporatesPaginated",
	async (
		{ page, pageSize, filter, sort }: CorporatesPayload,
		{ rejectWithValue, dispatch }
	) => {
		try {
			if (cancelTokenFetch) {
				cancelTokenFetch.cancel("Operazione cancellata")
				cancelTokenFetch = null
			}

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicorporate/v2/getCorporatesListPaginated`
			const parameters = new URLSearchParams()

			if (page === 0 || page) {
				parameters.append("page", page.toString())
			}
			if (pageSize) {
				parameters.append("size", pageSize.toString())
			}
			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<CorporatesListResponse> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<CorporatesListResponse>
			return rejectWithValue(axiosError?.response?.data ?? "error.generic")
		}
	}
)

export const fetchCorporates = createAsyncThunk<
	CorporatesListResponse,
	FetchCorporatesPayload,
	{ rejectValue: string }
>(
	"banks/fetchCorporates",
	async ({ url }: FetchCorporatesPayload, { dispatch, rejectWithValue }) => {
		try {
			cancelTokenFetch = axios.CancelToken.source()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				cancelToken: cancelTokenFetch.token,
				withCredentials: false,
			}
			const response: AxiosResponse<CorporatesListResponse> = await axios.get(
				url,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<CorporatesListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const updateCorporate = createAsyncThunk<
	ICreateResponse,
	IPatchPayload,
	{ rejectValue: AxiosError }
>(
	"corporate/patch",
	async (
		{ corporateData, idBank, idCorporate }: IPatchPayload,
		{ rejectWithValue }
	) => {
		try {
			const formData = new FormData()
			Object.keys(corporateData).forEach((key) => {
				formData.append(key, corporateData[key])
			})

			const headerConfig: AxiosRequestConfig = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				withCredentials: true,
			}
			const response: AxiosResponse = await axios.patch(
				`${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicorporate/v2/${idBank}/update/${idCorporate}`,
				formData,
				headerConfig
			)
			return response
		} catch (error) {
			return rejectWithValue(error?.response)
		}
	}
)
